/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Button, ButtonProps } from '@material-ui/core';
import styled from 'styled-components';
import { PikaIcon } from '../PikaIcon';

export const StyledButton = styled(Button)<ButtonProps>`
  &&& {
    font-family: 'Lato', sans-serif;
    padding: ${({ size }) => {
      if (size === 'small') {
        return '.4rem 1.4rem';
      }
      return '0.8rem 2.8rem';
    }};
    border-radius: 0;
    ${({ disabled }) => (disabled ? 'opacity: .5;' : '')}
  }
`;

export const PrimaryButton = styled(StyledButton)`
  &&& {
    background-color: #60dcc1;
    color: #fff;
    &:hover {
      background-color: #69f2d4;
    }
  }
`;

export const PrimaryTextButton = styled(StyledButton)`
  &&& {
    background-color: #fff;
    color: #60dcc1;
    &:hover {
      background-color: rgb(85 255 218 / 16%);
    }
  }
`;

export const CreateButton = styled(StyledButton)`
  &&& {
    padding: 0 1rem;
    background-color: #60dcc1;
    color: #fff;
    min-width: 65px;
    &:hover {
      background-color: #69f2d4;
    }

    span:nth-of-type(1) {
      display: inline-flex;
      justify-content: center;
      align-items: center;

      &:before {
        content: '+';
        font-weight: bold;
        font-size: 2.2rem;
      }
    }
  }
`;

export const SecondaryButton = styled(StyledButton)`
  &&& {
    background-color: #ececec;
    color: #333;
    &:hover {
      background-color: #d2d2d2;
    }
  }
`;

export const NegativeButton = styled(StyledButton)`
  &&& {
    background-color: #ef3340;
    color: #fff;
    &:hover {
      background-color: #f18c93;
    }
  }
`;

export const NegativeTextButton = styled(StyledButton)`
  &&& {
    background-color: #fff;
    color: #ef3340;
    &:hover {
      background-color: rgba(220, 0, 78, 0.04);
    }
  }
`;

export const InfoButton = styled(StyledButton)`
  &&& {
    background-color: #0076a5;
    color: #fff;
    &:hover {
      background-color: #66accc;
    }
  }
`;

type SaveCancelButtonProps = {
  variety: 'save' | 'secondary' | 'default';
};

export const StyledSaveCancelButton = styled(StyledButton)<
  SaveCancelButtonProps
>`
  &&& {
    background-color: ${props =>
      props.variety === 'save'
        ? '#0076a5'
        : props.variety === 'secondary'
        ? '#ececec'
        : '#000'};
    color: ${props =>
      props.variety === 'save'
        ? '#fff'
        : props.variety === 'secondary'
        ? '#333'
        : '#fff'};
    width: 100%;
    margin-bottom: 2rem;
    margin-top: 0;
    &:hover {
      background-color: ${props =>
        props.variety === 'save'
          ? '#66accc'
          : props.variety === 'secondary'
          ? '#d2d2d2'
          : '#ccc'};
    }
  }
`;

export const SaveCancelButton: React.FC<ButtonProps & {
  save?: boolean;
  secondary?: boolean;
}> = props => {
  return (
    <StyledSaveCancelButton
      {...{
        ...props,
        save: undefined,
        secondary: undefined,
        variety: props.save
          ? 'save'
          : props.secondary
          ? 'secondary'
          : 'default',
      }}
    >
      {props.children}
    </StyledSaveCancelButton>
  );
};

export const WarningButton = styled(StyledButton)`
  &&& {
    background-color: #ffb81c;
    color: #fff;
    &:hover {
      background-color: #fdd466;
    }
  }
`;

export interface IconButtonProps extends React.HTMLProps<HTMLButtonElement> {
  /**
   * For list of icons, visit --> {@link https://www.fizzywalthamstow.com/fonts/ss-pika/documentation.html}
   */
  icon: string;
}

const StyledIconButton = styled.button`
  background: none;
  border: none;
  color: #c9c9c9;
  padding: 0;
  cursor: pointer;
  outline: none;

  &:active,
  &:focus,
  &:hover {
    color: #a5a5a5;
    outline: none;
  }
`;

const StyledIconDeleteButton = styled(StyledIconButton)`
  &:hover {
    color: #ef3340;
  }
`;

export const SmallIconButton: React.FC<IconButtonProps> = ({
  icon,
  onClick,
  title,
}) => {
  return (
    <StyledIconDeleteButton type="button" title={title || ''} onClick={onClick}>
      <PikaIcon icon={icon} />
    </StyledIconDeleteButton>
  );
};

export type IconButtonsProps = {
  actions: IconButtonProps[];
};

export const IconButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 4px;
  & > button:not(:first-child) {
    margin-left: 5px;
  }
`;

export const IconButtons: React.FC<IconButtonsProps> = ({ actions }) => {
  return (
    <IconButtonsContainer>
      {actions.length > 0 &&
        actions.map((props: IconButtonProps, index: number) => {
          return <SmallIconButton key={`${props.icon}-${index}`} {...props} />;
        })}
    </IconButtonsContainer>
  );
};

const StyledPreviewAnchor = styled.a`
  &&& {
    &:link {
      text-decoration: none;
    }

    > button {
      margin-bottom: 1rem;
    }
  }
`;

export const PreviewButton: React.FC<{
  href: string;
}> = ({ href }) => {
  return (
    <StyledPreviewAnchor href={href} target="_blank" rel="noreferrer">
      <PrimaryButton size="small" fullWidth>
        <PikaIcon icon="ss-rightward" />
        &nbsp;Preview
      </PrimaryButton>
    </StyledPreviewAnchor>
  );
};
